// ./src/components/generic/ShortcutHintBar/ShortcutHintBar.tsx
//
// A standard, subtle strip of keyboard-shortcut hints shown at the bottom of a
// page so users can discover what's available. Data-driven — pass the hints
// that match the shortcuts actually wired on the page (build them with
// useEntityPageShortcutHints so they stay in sync). Keyboard-only, so it hides
// on phones (no physical keyboard).

import React from "react";
import { Box, Group, Text, Kbd } from "@mantine/core";
import { useTranslation } from "react-i18next";

export interface ShortcutHint {
  /** The key combo to display, e.g. "⌘F", "Ctrl+I", "F9". */
  keys: string;
  /** What the shortcut does, e.g. "Search". */
  label: string;
}

export interface ShortcutHintBarProps {
  /** Hints to render, in display order. Nothing renders when empty. */
  hints: ShortcutHint[];
  /** Optional leading label (defaults to a translated "Shortcuts"). */
  label?: string;
}

const ShortcutHintBarComponent: React.FC<ShortcutHintBarProps> = ({
  hints,
  label,
}) => {
  const { t } = useTranslation(["common"]);

  if (hints.length === 0) {
    return null;
  }

  return (
    <Box
      visibleFrom="sm"
      style={{
        borderTop: "1px solid var(--mantine-color-gray-2)",
        paddingTop: "0.5rem",
        marginTop: "0.25rem",
      }}
    >
      <Group gap="md" wrap="wrap" justify="center">
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: "0.04em" }}>
          {label ?? t("common:shortcuts.label", { defaultValue: "Shortcuts" })}
        </Text>

        {hints.map((hint) => (
          <Group key={`${hint.keys}-${hint.label}`} gap={6} wrap="nowrap">
            <Kbd size="xs">{hint.keys}</Kbd>
            <Text size="xs" c="dimmed">
              {hint.label}
            </Text>
          </Group>
        ))}
      </Group>
    </Box>
  );
};

export const ShortcutHintBar = React.memo(ShortcutHintBarComponent);
